import { useState, createContext, useContext, useEffect } from 'react';
import { ITranslations } from 'src/models/interfaces/ITranslations';
import getTranslation from 'src/translations';

interface IUserStore {
   userId: string;
   translation?: ITranslations;
   language: string;
   updateLanguage(language: string): void;
   login(id: string): void;
   logout(): void;
}

const useUserStore = (lang: string) => {
   const [userId, setUser] = useState<string>('');
   const [language, setLanguage] = useState(localStorage.getItem('language') ?? lang);

   useEffect(() => {
      let user = '';
      if ((localStorage.getItem('user') ?? '') !== '') {
         user = JSON.parse(localStorage.getItem('user') ?? '');
      }
      setUser(user);

      return () => {
         setUser('');
      };
   }, []);

   return {
      userId,
      language,
      updateLanguage: (language: string) => {
         localStorage.setItem('language', language);
         setLanguage(language);
      },
      translation: getTranslation(language.toLowerCase()),
      login: (id: string) => {
         localStorage.setItem('user', JSON.stringify(id));
         setUser(id);
      },
      logout: () => {
         localStorage.removeItem('user');
         setUser('');
      },
   };
};

const UserContext = createContext<IUserStore>({
   userId: '',
   language: 'English',
   updateLanguage: () => {},
   translation: undefined,
   login: () => {},
   logout: () => {},
});

export const UserContextProvider = ({
   children,
   language = 'English',
}: {
   children: JSX.Element | JSX.Element[];
   language?: string;
}) => <UserContext.Provider value={useUserStore(language)}>{children}</UserContext.Provider>;

export const useLanguage = () => useContext(UserContext)?.language;
export const useUpdateLanguage = () => useContext(UserContext)?.updateLanguage;
export const useTranslations = () => useContext(UserContext)?.translation;

export const useUser = () => useContext(UserContext)?.userId;
export const useLogin = () => useContext(UserContext)?.login;
export const useLogout = () => useContext(UserContext)?.logout;
